import { BaseModel } from './base-model';
import { AvailableFunnelItemStatuses, FunnelFulfillmentType, FunnelItemStatus } from './funnel';
import { CardType, TransactionPaymentSource } from './order';
import { SessionSource } from './session-enums';

export interface Audience extends BaseModel {
  name: string;
  updated: string;
  paths: AudiencePaths[];
  fulfillment_type: FunnelFulfillmentType;
  min_billing_cycle: number;
  max_billing_cycle: number;
  use_billing_cycle: boolean;
  is_3ds: boolean;
  merchant_ids: string[];
  cc_types: CardType[];
  payment_types: TransactionPaymentSource[];
  states: string[];
  countries: string[];
  crm_campaigns: string[] | number[];
  use_crm_campaign_filter: boolean;
  campaign_products: string[] | number[];
  use_product_filter: boolean;
  use_reason_filter: boolean;
  reason_categories: number[];
  traffic_sources: SessionSource[];
  funnel_item_statuses: FunnelItemStatus[];
  is_modified: boolean;
  aff_ids: string[];
  parent_path: string | number;
  status?: AudienceStatus;
  showThreeDotMenu?: boolean;
}

export enum AudienceType {
  Retention = 0,
  Phone = 1,
  Chat = 2,
  Lifeline = 3,
  SingleStep = 4,
  Resolve = 5,
}

/**
 * Per-brand display filters for a brand single step path. The audience is created and deleted with
 * the row; its filter values are edited through the audience itself.
 */
export interface SingleStepPathFilter {
  id: number;
  campaign: number;
  funnel: number;
  audience: Audience;
}

export interface AudiencePaths {
  id: number;
  name: string;
}

export interface AudienceCondition {
  abbr: string;
  label: string;
  value: string;
}

export interface SurveyResponseCategory {
  id: number;
  value: boolean;
  label: string;
  is_global: boolean;
}

export enum AudienceStatus {
  All = 0,
  Active = 1,
  Draft = 2,
  Inactive = 3,
  Assigned = 4,
  Unassigned = 5,
}

export const AudienceStatusLabels = {};
AudienceStatusLabels[AudienceStatus.All] = 'All';
AudienceStatusLabels[AudienceStatus.Active] = 'Active';
AudienceStatusLabels[AudienceStatus.Draft] = 'Draft';
AudienceStatusLabels[AudienceStatus.Inactive] = 'Inactive';
AudienceStatusLabels[AudienceStatus.Assigned] = 'Assigned';
AudienceStatusLabels[AudienceStatus.Unassigned] = 'Unassigned';

export enum AudienceDateTypes {
  LastModified = 'lastModified',
  DateCreated = 'dateCreated',
}

export enum AudiencePopup {
  Create = 0,
  Update = 1,
  Duplicate = 2,
  Delete = 3,
  Conditions = 4,
}

export enum TrafficSource {
  DIRECTLINK = 0,
  EMAIL = 1,
  PHONE = 2,
  SMS = 3,
  API = 4,
}

export enum AudienceConditionType {
  SurveyResponseCategories = 'Survey Response Categories',
  FulfillmentType = 'Fulfillment Type',
  BillingCycle = 'Billing Cycle',
  TransactionFilters = 'Transaction Filters',
  Location = 'Location Filters',
  CampaignFilters = 'Campaign Filters',
  ProductFilters = 'Product Filters',
  TrafficSource = 'Traffic Source',
  ItemStatus = 'Item Status Filters',
  UseProductFilters = 'ProductFilters',
  UseCampaignFilters = 'CampaignFilters',
}

/**
 * The condition groups an audience has values for, used to summarize it in listings.
 */
export function getAudienceConditions(audience: Audience): AudienceConditionType[] {
  return [
    audience.use_reason_filter &&
      audience.reason_categories.length > 0 &&
      AudienceConditionType.SurveyResponseCategories,
    audience.fulfillment_type && AudienceConditionType.FulfillmentType,
    ((audience.is_3ds ||
      (audience.merchant_ids && audience.merchant_ids.length > 0) ||
      (audience.cc_types && audience.cc_types.length > 0) ||
      (audience.payment_types && audience.payment_types.length > 0)) &&
      AudienceConditionType.TransactionFilters) as AudienceConditionType,
    audience.use_billing_cycle && AudienceConditionType.BillingCycle,
    audience.countries && audience.countries.length > 0 && AudienceConditionType.Location,
    audience.traffic_sources && audience.traffic_sources.length > 0 && AudienceConditionType.TrafficSource,
    audience.funnel_item_statuses && audience.funnel_item_statuses.length > 0 && AudienceConditionType.ItemStatus,
    audience.use_product_filter && AudienceConditionType.ProductFilters,
    audience.use_crm_campaign_filter && AudienceConditionType.CampaignFilters,
  ].filter(Boolean) as AudienceConditionType[];
}

export enum AudienceOutcome {
  CancelSubscriptionFullRefund = 1,
  CancelSubscriptionPartialRefund = 2,
  OpenRetentionFlow = 3,
  RedirectToSupport = 4,
  HideRefundRequest = 5,
}

export const AudienceOutcomeLabels: { [key in AudienceOutcome]: string } = {
  [AudienceOutcome.CancelSubscriptionFullRefund]: 'Cancel Subscription With Full Refund',
  [AudienceOutcome.CancelSubscriptionPartialRefund]: 'Cancel Subscription With Partial Refund',
  [AudienceOutcome.OpenRetentionFlow]: 'Open Retention Flow',
  [AudienceOutcome.RedirectToSupport]: 'Redirect To Support',
  [AudienceOutcome.HideRefundRequest]: 'Hide Refund Request',
};

// The outcomes a Resolve rule can pick, mirroring the backend's AudienceOutcome.resolve_outcomes.
export const AudienceResolveOutcomes: AudienceOutcome[] = [
  AudienceOutcome.OpenRetentionFlow,
  AudienceOutcome.CancelSubscriptionFullRefund,
  AudienceOutcome.CancelSubscriptionPartialRefund,
  AudienceOutcome.RedirectToSupport,
  AudienceOutcome.HideRefundRequest,
];

export enum AudienceBillingCycle {
  One = 1,
  Two = 2,
  Three = 3,
  Custom = 4,
}

export enum ProductSortOption {
  ProductName,
  ProductId,
  CampaignName,
  CampaignId,
  OfferId,
}

export const ProductSortOptionLabels = {
  [ProductSortOption.ProductName]: 'Product Name',
  [ProductSortOption.ProductId]: 'Product ID',
  [ProductSortOption.CampaignName]: 'Campaign Name',
  [ProductSortOption.CampaignId]: 'Campaign ID',
  [ProductSortOption.OfferId]: 'Offer ID',
};

export const BillingCycleCustomMin: number = 4;
export const BillingCycleNoMax: number = 9;
export const DefaultMinBillingCycle: number = 1;
export const EmptyBillingCycleValue: number = -1;

export const AudienceBillingCycleOptions: number[] = Array.from(
  { length: BillingCycleNoMax - DefaultMinBillingCycle + 1 },
  (_, index) => index + DefaultMinBillingCycle
);

export const AudienceCardTypeOptions = [
  { id: CardType.Visa, img: '/assets/audienceAssets/visa.png', label: 'Visa' },
  { id: CardType.Mastercard, img: '/assets/audienceAssets/master.png', label: 'Mastercard' },
  { id: CardType.Amex, img: '/assets/audienceAssets/amex.png', label: 'American Express' },
  { id: CardType.Discover, img: '/assets/audienceAssets/discover.png', label: 'Discover' },
  { id: CardType.Other, label: 'Other' },
  { id: CardType.None, label: 'No Card' },
];

export const AudiencePaymentTypeOptions = [
  { id: TransactionPaymentSource.GooglePay, img: '/assets/audienceAssets/google-pay-icon.svg', label: 'Google Pay' },
  { id: TransactionPaymentSource.ApplePay, img: '/assets/audienceAssets/apple-pay-icon.svg', label: 'Apple Pay' },
  { id: TransactionPaymentSource.Paypal, img: '/assets/audienceAssets/paypal-icon.svg', label: 'PayPal' },
  { id: TransactionPaymentSource.DirectDebit, label: 'Direct Debit' },
  { id: TransactionPaymentSource.Prepaid, label: 'Prepaid' },
  { id: TransactionPaymentSource.Other, label: 'Other' },
];

export const AudienceTrafficSourceOptions = [
  { id: TrafficSource.EMAIL, label: 'Email' },
  { id: TrafficSource.PHONE, label: 'Phone' },
  { id: TrafficSource.DIRECTLINK, label: 'Direct Link' },
];

export const AudienceProductBatchSize = 200;

export const AudienceDropdownSettings = {
  singleSelection: false,
  allowSearchFilter: true,
  enableCheckAll: false,
};

// Splits typed ids (comma, semicolon or newline separated) into trimmed, non-empty ids.
export function splitAudienceIds(value: string | string[]): string[] {
  const ids = Array.isArray(value) ? value : (value || '').split(/[\n;,]/);

  return ids.map((id) => id.trim()).filter((id) => id.length > 0);
}

export const AudienceFilterDescriptions = {
  SurveyResponseCategories: {
    tooltip: '',
    helpingText: 'Choose categories to include linked survey reasons automatically.',
  },
  FulfillmentType: {
    tooltip: 'Filter by fulfillment type of the purchased product.',
    helpingText: 'Select if this applies to shippable or digital items.',
  },
  BillingCycle: {
    tooltip: 'Target customers based on their current subscription cycle.',
    helpingText: 'Target customers by their subscription cycle.',
  },
  TransactionFilters: {
    tooltip: 'Filter based on transactions details.',
    helpingText: 'Filter by payment method or verification details.',
    verificationTip: 'Check to filter the product path by 3DS verified orders only',
    merchantIdsTip:
      'Enter each CRM Merchant ID, separated by comma or newline, that are allowed to enter this path. ' +
      'Leave blank to allow all merchant IDs.',
    cardTip: 'Select the credit card types to match against.',
    paymentTypeTip: 'Select the payment methods to match against.',
  },
  Location: {
    tooltip: 'Target users by their shipping country or state/province.',
    helpingText: 'Filter by shipping country or state.',
    stateTip: 'Select the states to match against. Leave blank to match all states.',
  },
  CampaignFilters: {
    tooltip: 'Targets customers based on a specific CRM campaign.',
    helpingText: 'Target customers from selected marketing campaigns.',
  },
  ProductFilters: {
    tooltip: 'Target customers based on products they’ve purchased.',
    helpingText: 'Show only to customers who purchased specific products.',
  },
  TrafficSource: {
    tooltip: 'Filter users by how they entered the support flow.',
    helpingText: 'Filter users by how they entered the retention flow (email, phone, direct link, etc.).',
  },
  ItemStatus: {
    tooltip: "Filter by the item's current status.",
    helpingText: 'Show this single step only for items in the selected statuses.',
  },
};

export const AudienceItemStatusOptions = AvailableFunnelItemStatuses;

export const CrudPopupTitleMap: Record<number, string> = {
  [AudiencePopup.Create]: 'Create Segment',
  [AudiencePopup.Update]: 'Rename Segment',
  [AudiencePopup.Duplicate]: 'Duplicate Segment',
  [AudiencePopup.Delete]: 'Delete Segment',
  [AudiencePopup.Conditions]: 'Conditions',
};
